package com.bikompanion.data.parking

import com.bikompanion.data.overpassQuery
import com.bikompanion.data.parseBikeParking
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.ParkingRepository

/**
 * Reads bike racks straight from Overpass.
 *
 * Common code over an [OverpassTransport], not an `expect class`. As three actuals this was the
 * same file twice on the JVM targets and a third that had quietly drifted — web ran with no
 * request timeout at all — and there was no seam to test any of the retry behaviour at. What a
 * platform actually owns is one POST; everything else is policy that should behave identically
 * everywhere, and now does.
 *
 * Parsing happens inside the attempt on purpose: Overpass answers a throttle with an HTML page
 * and a 200 on some mirrors, so a body that won't parse has to count as that instance being down
 * rather than as an answer. See [OverpassEndpointRacer].
 *
 * Uncached by design: [ParkingRepositoryImpl] is what the rest of the app injects, and keeping
 * the trip to the network separate from the decision to make it keeps both testable.
 */
internal class OverpassParkingRepository(
    private val transport: OverpassTransport,
    private val racer: OverpassEndpointRacer = OverpassEndpointRacer(),
) : ParkingRepository {
    override suspend fun fetchBikeParking(bounds: BoundingBox): List<BikeParkingSpot> {
        val query = overpassQuery(bounds.south, bounds.west, bounds.north, bounds.east)
        return racer.race { endpoint -> parseBikeParking(transport.post(endpoint, query)) }
    }
}
